import { flatten, isNil, uniq } from 'lodash-es'
import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  AliasDetail,
  Collateral,
  CollateralPrice,
  CrudEventEnum,
  EntityType,
  EntityTypeEnum,
  EntityWrapper,
  FatFingerUserLimit,
  FatFingerUserLimitDetail,
  Organisation,
  OrganisationMeta,
  OrganisationMetaDetail,
  OrganisationType,
  PendingCollateralDetail,
  PendingTradingEntityDetail,
  PrimeBroker,
  ReferenceDataEntityType,
  ReferenceDataManagementDataUpdate,
  ReferenceDataManagementEvent,
  TradingEntity,
  TradingEntityClearing,
  TradingEntityClearingDetail,
  TradingEntityClearingType,
  TradingEntityMeta,
  TradingRelationship,
  TradingRelationshipDetail,
  ValidationIssue,
  WorkflowDetail
} from 'api'
import { GlobalState } from 'store'
import { createAllEntitiesSelector, createEntityGetterSelector, createEntityUpdateGetterSelector } from './entitySelectors'
import { tradingEntityClearingDetailsGetterSelector } from './clearingSelectors'
import { EntityTypeFor, FICC_CLEARING_HOUSE_ID, TradingEntityHelper } from 'model'
import { compareInsensitive, getDefaultFatFingerSector, NEW_ITEM_PREFIX, notNilFilter, ParametricSelector } from 'utils'
import { organisationMetaDetailsGetterSelector } from './organisationSelectors/organisationMetaSelector'
import { aliasesDetailsGetterSelector } from './tradingEntitySelectors/tradingEntityAliasesSelectors'

const referenceDataManagementUpdateGetterSelector = createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA)

export const workflowReferenceDataEntitiesGetterSelector: Selector<GlobalState, (workflowId: string) => EntityWrapper[]> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  getReferenceDataManagementData => (workflowId: string) => {
    const referenceData = getReferenceDataManagementData(workflowId)
    return referenceData ? referenceData.referenceDataEntities : []
  }
)

export const createWorkflowReferenceDataAvailableEventsSelector = <TProps>(workflowIdSelector: ParametricSelector<{}, TProps, string>) => {
  return createSelector(
    [workflowIdSelector, createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA)],
    (workflowId, getReferenceDataManagementData) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      return referenceData ? referenceData.availableEvents : []
    }
  )
}

export const workflowReferenceDataAvailableEventsGetterSelector: Selector<
  GlobalState,
  (workflowId: string) => ReferenceDataManagementEvent[]
> = createSelector(
  [createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA)],
  getReferenceDataManagementData => (workflowId: string) => {
    const referenceData = getReferenceDataManagementData(workflowId)
    return referenceData ? referenceData.availableEvents : []
  }
)

export const createWorkflowReferenceDataValidationIssuesSelector = <TProps>(workflowIdSelector: ParametricSelector<{}, TProps, string>) => {
  return createSelector(
    [workflowIdSelector, createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA)],
    (workflowId, getReferenceDataManagementData) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      return flatten(referenceData?.entity.referenceDataEntities.map(entity => entity.validationIssues ?? [])) ?? []
    }
  )
}

export const workflowReferenceDataValidationIssuesGetterSelector: Selector<
  GlobalState,
  (workflowId: string) => ValidationIssue[]
> = createSelector(
  [createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA)],
  getReferenceData => (workflowId: string) => {
    const referenceData = getReferenceData(workflowId)
    return flatten(referenceData?.entity.referenceDataEntities.map(entity => entity.validationIssues ?? [])) ?? []
  }
)

export const workflowReferenceDataEntitiesByTypeGetterSelector: Selector<
  GlobalState,
  <TEntity extends EntityTypeEnum>(workflowId: string, entityTypeEnum: TEntity) => ReferenceDataEntityType<TEntity>[]
> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  getReferenceDataManagementData => (workflowId: string, entityTypeEnum: EntityTypeEnum) => {
    const referenceData = getReferenceDataManagementData(workflowId)

    if (referenceData) {
      return referenceData.referenceDataEntities.filter(
        (referenceDataEntity: EntityWrapper) => referenceDataEntity.entityTypeEnum === entityTypeEnum
      )
    }
    return []
  }
)

export const createWorkflowReferenceDataEntitySelector = <TProps, TEntityType extends EntityType>(
  entityType: TEntityType,
  workflowIdSelector: ParametricSelector<{}, TProps, string>,
  entityIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, ReferenceDataEntityType<EntityTypeFor<TEntityType>> | null> => {
  return createSelector(
    workflowIdSelector,
    entityIdSelector,
    createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    (workflowId, entityId, getReferenceDataManagementData) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      if (referenceData) {
        const entity = referenceData.referenceDataEntities.find(
          referenceDataEntity => referenceDataEntity.entityTypeEnum === entityType && referenceDataEntity.id === entityId
        )
        if (entity) {
          return entity as ReferenceDataEntityType<EntityTypeFor<TEntityType>>
        }
      }
      return null
    }
  )
}

export const createWorkflowReferenceDataEntitiesSelector = <TProps, TEntityType extends EntityType>(
  entityType: TEntityType,
  workflowIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, ReferenceDataEntityType<EntityTypeFor<TEntityType>>[]> => {
  return createSelector(
    workflowIdSelector,
    createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    (workflowId, getReferenceDataManagementData) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      if (referenceData) {
        return referenceData.referenceDataEntities.filter(
          referenceDataEntity => referenceDataEntity.entityTypeEnum === entityType
        ) as ReferenceDataEntityType<EntityTypeFor<TEntityType>>[]
      }
      return []
    }
  )
}

export const createWorkflowTradingRelationshipDetailsSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>,
  organisationTypeSelector: ParametricSelector<{}, TProps, OrganisationType>,
  tradingEntityIdSelector: ParametricSelector<{}, TProps, string>
) => {
  return createSelector(
    [
      workflowIdSelector,
      organisationTypeSelector,
      tradingEntityIdSelector,
      createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
      createEntityGetterSelector(EntityType.TRADING_ENTITY),
      createEntityGetterSelector(EntityType.ORGANISATION)
    ],
    (workflowId, organisationType, workflowTradingEntityId, getReferenceDataManagementDataUpdate, getTradingEntity, getOrganisation) => {
      const referenceDataUpdate = getReferenceDataManagementDataUpdate(workflowId)

      if (!referenceDataUpdate) {
        return []
      }

      const buySideOrg = organisationType === OrganisationType.BUY_SIDE

      return referenceDataUpdate.entity.referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_RELATIONSHIP)
        .map((referenceDataEntity: ReferenceDataEntityType<TradingRelationship>): TradingRelationship => referenceDataEntity.data)
        .filter(tradingRelationship =>
          buySideOrg
            ? tradingRelationship.buyTradingEntity === workflowTradingEntityId
            : tradingRelationship.sellTradingEntity === workflowTradingEntityId
        )
        .map<TradingRelationshipDetail>(tradingRelationship => {
          const buyTradingEntity = getTradingEntity(tradingRelationship.buyTradingEntity)
          const sellTradingEntity = getTradingEntity(tradingRelationship.sellTradingEntity)
          return {
            tradingRelationship: tradingRelationship,
            buyTradingEntity,
            buySideOrg: isNil(buyTradingEntity) ? null : getOrganisation(buyTradingEntity.organisationId),
            sellSideOrg: isNil(sellTradingEntity) ? null : getOrganisation(sellTradingEntity.organisationId),
            sellTradingEntity,
            organisationType,
            availableEvents: referenceDataUpdate.availableEvents
          }
        })
    }
  )
}

export const createWorkflowFatFingerUserLimitDetailsSelector = <TProps>(workflowIdSelector: ParametricSelector<{}, TProps, string>) => {
  return createSelector(
    [
      workflowIdSelector,
      createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
      createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
      createEntityGetterSelector(EntityType.PRODUCT),
      createAllEntitiesSelector(EntityType.FAT_FINGER_PRODUCT_SECTOR)
    ],
    (workflowId, getReferenceDataManagementDataUpdate, getProductSector, getProduct, fatFingers) => {
      const referenceDataUpdate = getReferenceDataManagementDataUpdate(workflowId)

      if (!referenceDataUpdate) {
        return []
      }

      return referenceDataUpdate.entity.referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.FAT_FINGER_USER)
        .map((referenceDataEntity: ReferenceDataEntityType<FatFingerUserLimit>): FatFingerUserLimit => referenceDataEntity.data)
        .map<FatFingerUserLimitDetail>(fatFingerUserLimit => {
          const productSector = getProductSector(fatFingerUserLimit.productSectorId)
          const product = productSector && getProduct(productSector.productId)
          return {
            fatFingerUserLimit: fatFingerUserLimit,
            productSector,
            defaultFatFinger: getDefaultFatFingerSector(fatFingers, fatFingerUserLimit.productSectorId),
            product
          }
        })
        .sort((tem1, tem2) => {
          return compareInsensitive(tem1.productSector?.name || '', tem2.productSector?.name || '')
        })
    }
  )
}

export const createWorkflowTradingEntityMetasSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, TradingEntityMeta[]> => {
  return createSelector(
    workflowIdSelector,
    createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    (workflowId, getReferenceDataManagementData, getTradingEntity) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      if (!referenceData) {
        return []
      }

      const workflowTradingEntity: TradingEntity | null =
        referenceData.referenceDataEntities.find(
          referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY
        )?.data ?? null
      if (!workflowTradingEntity) {
        return []
      }

      const workflowTradingEntityMetas: TradingEntityMeta[] = referenceData.referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_META)
        .map(referenceDataEntity => referenceDataEntity.data)

      const workflowTradingRelationships: TradingRelationship[] = referenceData.referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_RELATIONSHIP)
        .map(referenceDataEntity => referenceDataEntity.data)

      const relatedCounterpartyIdsWithoutMeta = uniq(
        workflowTradingRelationships
          .map(tradingRelationship =>
            tradingRelationship.buyTradingEntity === workflowTradingEntity.id
              ? tradingRelationship.sellTradingEntity
              : tradingRelationship.buyTradingEntity
          )
          .filter(
            counterpartyId =>
              !workflowTradingEntityMetas.map(tradingEntityMeta => tradingEntityMeta.subjectTradingEntityId).includes(counterpartyId)
          )
      )

      const missingTradingRelationships = relatedCounterpartyIdsWithoutMeta.map(counterpartyId => ({
        id: `${NEW_ITEM_PREFIX}-${workflowTradingEntity.id}-${counterpartyId}`,
        aliasTypeAndValues: [],
        ownerTradingEntityId: workflowTradingEntity.id,
        subjectTradingEntityId: counterpartyId,
        primaryCoverUserId: null,
        secondaryCoverUserId: null,
        primeBrokerId: null
      }))

      return workflowTradingEntityMetas.concat(missingTradingRelationships).sort((tem1, tem2) => {
        const tem1Subject = getTradingEntity(tem1.subjectTradingEntityId)
        const tem2Subject = getTradingEntity(tem2.subjectTradingEntityId)
        const tem1SubjectName = tem1Subject ? TradingEntityHelper.getName(tem1Subject) : ''
        const tem2SubjectName = tem2Subject ? TradingEntityHelper.getName(tem2Subject) : ''
        return compareInsensitive(tem1SubjectName, tem2SubjectName)
      })
    }
  )
}

export const createWorkflowAliasesDetailsSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, WorkflowDetail<AliasDetail>[]> => {
  return createSelector(
    workflowIdSelector,
    workflowTradingEntityMetasGetterSelector,
    aliasesDetailsGetterSelector,
    referenceDataManagementUpdateGetterSelector,
    (workflowId, getWorkflowTradingEntityMetas, getAliasesDetails, getReferenceDataManagementUpdate) => {
      const referenceDataUpdate: ReferenceDataManagementDataUpdate | null = getReferenceDataManagementUpdate(workflowId)

      const workflowTradingEntityMetas = getWorkflowTradingEntityMetas(workflowId)

      return getAliasesDetails(workflowTradingEntityMetas).map(alias => ({
        ...alias,
        availableEvents: referenceDataUpdate?.availableEvents ?? []
      }))
    }
  )
}

export const workflowTradingEntityMetasGetterSelector: Selector<GlobalState, (workflowId: string) => TradingEntityMeta[]> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  getReferenceData => (workflowId: string) => {
    const referenceData = getReferenceData(workflowId)

    if (!referenceData) {
      return []
    }
    const workflowTradingEntity: TradingEntity | null =
      referenceData.referenceDataEntities.find(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY)
        ?.data ?? null

    if (!workflowTradingEntity) {
      return []
    }

    const workflowTradingEntityMetas: TradingEntityMeta[] = referenceData.referenceDataEntities
      .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_META)
      .map(referenceDataEntity => referenceDataEntity.data)

    return workflowTradingEntityMetas
  }
)

export const workflowTradingEntityMetaGetterSelector: Selector<
  GlobalState,
  (workflowId: string, metaId: string) => TradingEntityMeta | null
> = createSelector(workflowTradingEntityMetasGetterSelector, getWorkflowTradingEntityMetas => (workflowId: string, metaId: string) => {
  const allWorkflowMetas = getWorkflowTradingEntityMetas(workflowId)

  const meta = allWorkflowMetas.find(m => m.id === metaId)

  return meta ?? null
})

export const workflowTradingEntityClearingDetailsSelector: Selector<
  GlobalState,
  (workflowId: string) => WorkflowDetail<TradingEntityClearingDetail>[]
> = createSelector(
  [createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA), tradingEntityClearingDetailsGetterSelector],
  (getReferenceDataManagementDataUpdate, getTradingEntityClearingDetails) => (workflowId: string) => {
    const referenceDataUpdate = getReferenceDataManagementDataUpdate(workflowId)

    if (!referenceDataUpdate) {
      return []
    }

    const tradingEntityClearings = referenceDataUpdate.entity.referenceDataEntities
      .filter((referenceDataEntity: EntityWrapper) => referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_CLEARING)
      .map((referenceDataEntity: ReferenceDataEntityType<TradingEntityClearing>): TradingEntityClearing => referenceDataEntity.data)

    return getTradingEntityClearingDetails(tradingEntityClearings).map((tradingEntityClearingDetail: TradingEntityClearingDetail) => {
      return {
        ...tradingEntityClearingDetail,
        availableEvents: referenceDataUpdate.availableEvents
      }
    })
  }
)

export const createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>,
  tradingEntityClearingTypeSelector: ParametricSelector<{}, TProps, TradingEntityClearingType>
): ParametricSelector<GlobalState, TProps, string[]> => {
  return createSelector(
    workflowIdSelector,
    tradingEntityClearingTypeSelector,
    createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    createAllEntitiesSelector(EntityType.CLEARING_HOUSE),
    createAllEntitiesSelector(EntityType.SPONSOR),
    (workflowId, tradingEntityClearingType, getReferenceDataManagementData, allClearingHouses, allSponsors) => {
      const referenceData = getReferenceDataManagementData(workflowId)

      if (!referenceData) {
        return []
      }

      if (tradingEntityClearingType === TradingEntityClearingType.DIRECT) {
        const allWorkflowClearingHouseIds = referenceData.referenceDataEntities
          .filter(
            (referenceDataEntity: EntityWrapper) =>
              referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_CLEARING &&
              referenceDataEntity.data.type === TradingEntityClearingType.DIRECT
          )
          .map(referenceDataEntity => referenceDataEntity.data.clearingHouseId)

        const allClearingHouseIds = allClearingHouses
          .filter(clearingHouse => clearingHouse.id !== FICC_CLEARING_HOUSE_ID)
          .map(clearingHouse => clearingHouse.id)

        return allClearingHouseIds.filter(clearingHouseId => !allWorkflowClearingHouseIds.includes(clearingHouseId))
      } else {
        const allWorkflowTradingEntityClearings = referenceData.referenceDataEntities.filter(
          (referenceDataEntity: EntityWrapper) =>
            referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_CLEARING &&
            referenceDataEntity.data.type === TradingEntityClearingType.SPONSORED
        )

        const allClearingHouseIds = allClearingHouses
          .filter(clearingHouse => clearingHouse.id !== FICC_CLEARING_HOUSE_ID)
          .map(clearingHouse => clearingHouse.id)

        return allClearingHouseIds.filter(
          clearingHouseId =>
            allWorkflowTradingEntityClearings.filter(
              (referenceDataEntity: ReferenceDataEntityType<TradingEntityClearing>) =>
                referenceDataEntity.data.clearingHouseId === clearingHouseId
            ).length < allSponsors.length
        )
      }
    }
  )
}

export const createWorkflowAvailableSponsoredTradingEntityClearingSponsorIdsSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>,
  entityIdSelector: ParametricSelector<{}, TProps, string>
) => {
  return createSelector(
    [
      workflowIdSelector,
      createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
      createAllEntitiesSelector(EntityType.SPONSOR),
      createEntityGetterSelector(EntityType.CLEARING_HOUSE),
      createWorkflowReferenceDataEntitySelector(EntityType.TRADING_ENTITY_CLEARING, workflowIdSelector, entityIdSelector)
    ],
    (workflowId, getReferenceDataManagementData, allSponsors, getClearingHouse, workflowEntity) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      if (!referenceData) {
        return []
      }

      const allWorkflowTradingEntityClearings = referenceData.referenceDataEntities.filter(
        (referenceDataEntity: EntityWrapper) =>
          referenceDataEntity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY_CLEARING &&
          referenceDataEntity.data.type === TradingEntityClearingType.SPONSORED
      )

      if (!workflowEntity) {
        return []
      }

      const allWorkflowTradingEntityClearingsSponsorsWithClearingHouseId = allWorkflowTradingEntityClearings
        .filter((tec: ReferenceDataEntityType<TradingEntityClearing>) => {
          return tec.data.sponsorId && tec.data.clearingHouseId === workflowEntity.data.clearingHouseId
        })
        .map((tec: ReferenceDataEntityType<TradingEntityClearing>) => tec.data.sponsorId)

      const clearingHouse = getClearingHouse(workflowEntity.data.clearingHouseId)
      const availableSponsorIds = allSponsors
        .filter(sponsor => !clearingHouse || sponsor.clearingHouseIds.includes(clearingHouse.id))
        .map(sponsor => sponsor.id)

      return availableSponsorIds.filter(sponsorId => !allWorkflowTradingEntityClearingsSponsorsWithClearingHouseId.includes(sponsorId))
    }
  )
}

export const workflowCollateralDetailsGetterSelector: Selector<
  GlobalState,
  (workflowIds: string[]) => PendingCollateralDetail[]
> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  createEntityGetterSelector(EntityType.INDEX),
  (getReferenceDataManagementData, getProductSector, getProduct, getIndex) => (workflowIds: string[]) =>
    workflowIds
      .map(workflowId => getReferenceDataManagementData(workflowId))
      .filter(notNilFilter)
      .map(referenceDataManagementData => {
        const collateral: Collateral | undefined = referenceDataManagementData.referenceDataEntities.find(
          entity => entity.entityTypeEnum === EntityTypeEnum.COLLATERAL
        )?.data
        const collateralPrice: CollateralPrice | undefined = referenceDataManagementData.referenceDataEntities.find(
          entity => entity.entityTypeEnum === EntityTypeEnum.COLLATERAL_PRICE
        )?.data

        if (!collateral || !collateralPrice) {
          throw new Error('Collateral/Collateral Price entity not found in reference data management data')
        }

        const productSector = collateral.productSectorId ? getProductSector(collateral.productSectorId) : null
        const product = productSector ? getProduct(productSector.productId) : null

        const index = collateral.collateralIndexDetails?.indexId ? getIndex(collateral.collateralIndexDetails.indexId) : null

        const validationIssues = flatten(referenceDataManagementData.referenceDataEntities.map(entity => entity.validationIssues)).filter(
          notNilFilter
        )

        return {
          collateral,
          collateralPrice,
          productSector,
          product,
          index,
          validationIssues
        }
      })
)

export const workflowTradingEntityDetailsGetterSelector: Selector<
  GlobalState,
  (workflowIds: string[]) => PendingTradingEntityDetail[]
> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  createEntityGetterSelector(EntityType.ORGANISATION),
  (getReferenceDataManagementData, getOrganisation) => (workflowIds: string[]) =>
    workflowIds
      .map(workflowId => getReferenceDataManagementData(workflowId))
      .filter(notNilFilter)
      .map(referenceDataManagementData => {
        const tradingEntity: TradingEntity | undefined = referenceDataManagementData.referenceDataEntities.find(
          entity => entity.entityTypeEnum === EntityTypeEnum.TRADING_ENTITY
        )?.data

        if (!tradingEntity) {
          throw new Error('Trading Entity entity not found in reference data management data')
        }

        const organisation = tradingEntity.organisationId ? getOrganisation(tradingEntity.organisationId) : null

        const validationIssues = flatten(referenceDataManagementData.referenceDataEntities.map(entity => entity.validationIssues)).filter(
          notNilFilter
        )

        return {
          tradingEntity,
          organisation,
          validationIssues
        }
      })
)

export const workflowValidationIssuesGetterSelector: Selector<GlobalState, (workflowIds: string[]) => ValidationIssue[][]> = createSelector(
  createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
  getReferenceDataManagementData => (workflowIds: string[]) =>
    workflowIds
      .map(workflowId => getReferenceDataManagementData(workflowId))
      .filter(notNilFilter)
      .map(referenceDataManagementData => {
        return flatten(referenceDataManagementData.referenceDataEntities.map(entity => entity.validationIssues)).filter(notNilFilter)
      })
)

export const createWorkflowPrimeBrokersSelector = <TProps>(
  workflowIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, PrimeBroker[]> => {
  return createSelector(
    workflowIdSelector,
    createEntityGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    (workflowId, getReferenceDataManagementData) => {
      const referenceData = getReferenceDataManagementData(workflowId)
      if (!referenceData) {
        return []
      }

      const workflowOrganisation: Organisation | null =
        referenceData.referenceDataEntities.find(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.ORGANISATION)
          ?.data ?? null
      if (!workflowOrganisation) {
        return []
      }

      return referenceData.referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.PRIME_BROKER)
        .map(referenceDataEntity => referenceDataEntity.data)
        .filter((primeBroker: PrimeBroker) => primeBroker.organisationId === workflowOrganisation.id)
    }
  )
}

// Orders the organisation meta, entries with "ALL" are sorted later
function getOrganisationMetaDetailSortKey(organisationMetaDetail: OrganisationMetaDetail) {
  const {
    ownerTradingEntityId,
    subjectOrganisationId,
    subjectTradingEntityId,
    productId,
    productSectorId
  } = organisationMetaDetail.organisationMeta
  return `${ownerTradingEntityId ?? 'zzz'}-${subjectOrganisationId ?? 'zzz'}-${subjectTradingEntityId ?? 'zzz'}-${productId ??
    'zzz'}-${productSectorId ?? 'zzz'}`
}

export const createWorkflowOrganisationMetaDetailsSelector = <TProps>(workflowIdSelector: ParametricSelector<{}, TProps, string>) => {
  return createSelector(
    [
      workflowIdSelector,
      createEntityUpdateGetterSelector(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
      organisationMetaDetailsGetterSelector
    ],
    (workflowId, getReferenceDataManagementDataUpdate, getOrganisationMetaDetails) => {
      const referenceDataUpdate = getReferenceDataManagementDataUpdate(workflowId)
      if (!referenceDataUpdate) {
        return []
      }

      const referenceDataEntities = referenceDataUpdate.entity.referenceDataEntities

      const workflowOrganisation: Organisation | null =
        referenceDataEntities.find(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.ORGANISATION)?.data ?? null
      if (!workflowOrganisation) {
        return []
      }

      const organisationMetas: OrganisationMeta[] = referenceDataEntities
        .filter(referenceDataEntity => referenceDataEntity.entityTypeEnum === EntityTypeEnum.ORGANISATION_META)
        .map(referenceDataEntity => referenceDataEntity.data)
        .filter((organisationMeta: OrganisationMeta) => organisationMeta.ownerOrganisationId === workflowOrganisation.id)

      return getOrganisationMetaDetails(organisationMetas)
        .map(organisationMetaDetail => ({
          ...organisationMetaDetail,
          availableEvents: referenceDataUpdate.availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
            ? [CrudEventEnum.UPSERT, CrudEventEnum.DELETE]
            : []
        }))
        .sort((omd1, omd2) => compareInsensitive(getOrganisationMetaDetailSortKey(omd1), getOrganisationMetaDetailSortKey(omd2)))
    }
  )
}
